import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { clsx } from '@a-type/ui';
import { AnimatePresence, motion, useMotionTemplate, useTransform, } from 'motion/react';
import { createContext, memo, useContext, useEffect, useMemo, } from 'react';
import { createPortal } from 'react-dom';
import { useMergedRef } from '../hooks/useMergedRef.js';
import { useBindBounds, useTagBounds } from './bounds.js';
import { draggableDataRegistry } from './dataRegistry.js';
import { useDndStore } from './dndStore.js';
import cls from './Draggable.module.css';
import { gesture } from './gestureStore.js';
import { TAGS } from './tags.js';
import { flipTransition } from './transitions.js';
import { useDragGesture, } from './useDragGesture.js';
function DraggableRoot({ id, data, disabled = false, noHandle = false, children, handleProps, tags, dropOnTag = TAGS.DROPPABLE, svg = false, ...rest }) {
    const { isDragged, isCandidate, ctxValue } = useDraggableRoot({
        id,
        data,
        disabled,
        dropOnTag,
        svg,
    });
    return (_jsx(DraggableContext.Provider, { value: ctxValue, children: _jsx(DndOverlayPortal, { enabled: isDragged || isCandidate, dragDisabled: disabled, id: id, tags: tags, ...rest, children: _jsx(ConditionalHandle, { disabled: noHandle, ...handleProps, children: children }) }) }));
}
function useDraggableRoot({ id, data, disabled, dropOnTag, svg, }) {
    const isDragged = useDndStore((state) => state.dragging === id);
    const isCandidate = useDndStore((state) => state.candidate === id);
    useEffect(() => draggableDataRegistry.register(id, data), [id, data]);
    const ctxValue = useMemo(() => ({
        id,
        data,
        disabled,
        dropOnTag,
        svg,
    }), [id, disabled, dropOnTag, svg]);
    return { isDragged, isCandidate, ctxValue };
}
const DraggableContext = createContext(null);
export function useDraggableContext() {
    const context = useContext(DraggableContext);
    if (!context) {
        throw new Error('useDraggableContext must be used within a Draggable component');
    }
    return context;
}
function DragMotion(props) {
    const { svg } = useDraggableContext();
    if (svg) {
        return _jsx(motion.g, { ...props });
    }
    return _jsx(motion.div, { ...props });
}
const allowDragInTags = [TAGS.DRAG_INTERACTIVE];
function DraggableHandle({ children, activationConstraint, allowStartFromDragIn = false, className, touchOffset, ref: userRef, onTap, ...rest }) {
    const { id, dropOnTag, svg } = useDraggableContext();
    const { ref, isCandidate, isDragging, disabled } = useDragGesture({
        activationConstraint,
        allowStartFromDragIn,
        touchOffset,
        onTap,
        dropOnTag,
    });
    const finalRef = useMergedRef(ref, userRef);
    useTagBounds(id, allowDragInTags, !allowStartFromDragIn);
    return (_jsx(DragMotion, { style: {
            touchAction: disabled ? 'initial' : 'none',
            pointerEvents: isCandidate ? 'none' : 'auto',
        }, onContextMenuCapture: disabled
            ? undefined
            : ((e) => {
                e.preventDefault();
                e.stopPropagation();
            }), ref: finalRef, role: "button", "aria-roledescription": "draggable", "aria-describedby": "dnd-instructions", tabIndex: 0, "data-draggable-handle": true, "data-disabled": disabled, "data-candidate": isCandidate, "data-dragging": isDragging, className: clsx(cls.handle, !disabled && cls.handleGrabMonitor, className), ...rest, children: children }));
}
const ConditionalHandle = ({ disabled, children, ...rest }) => {
    if (disabled) {
        return (_jsx(motion.div, { "data-disabled-handle": true, className: "w-full h-full", children: children }));
    }
    return (_jsx(DraggableHandle, { className: "w-full h-full", ...rest, children: children }));
};
export const Draggable = Object.assign(DraggableRoot, {
    Handle: DraggableHandle,
    ConditionalHandle,
});
const defaultTags = [TAGS.DRAGGABLE];
/**
 * Selectively portals the dragged element to the overlay layer if it is being dragged.
 * Applies local (relative) movement to non-portaled content if any.
 */
const DndOverlayPortal = memo(function DndOverlayPortal({ children, enabled, DraggedContainer, draggedClassName, className, id, dragDisabled, movedBehavior = 'remove', ref, tags = defaultTags, ...rest }) {
    const { svg } = useDraggableContext();
    const overlayEl = useDndStore((state) => svg ? state.svgOverlayElement : state.domOverlayElement);
    const isPortaling = enabled && !!overlayEl;
    const bindBounds = useBindBounds(id);
    useTagBounds(id, tags);
    const mainRef = useMergedRef(ref, dragDisabled ? undefined : bindBounds);
    return (_jsxs(_Fragment, { children: [isPortaling &&
                createPortal(_jsx(DraggedRoot, { Container: DraggedContainer, ...rest, className: clsx(cls.noPointerEvents, className, draggedClassName), children: children }), overlayEl), _jsx(DragMotion, { layoutId: isPortaling ? undefined : id, transition: flipTransition, "data-disabled": dragDisabled, "data-draggable": id, "data-is-moved": isPortaling, ref: mainRef, className: clsx({
                    [cls.removed]: isPortaling && movedBehavior === 'remove',
                    [cls.moved]: isPortaling && movedBehavior === 'fade',
                }, className), animate: {
                    width: isPortaling && movedBehavior === 'remove' ? 0 : undefined,
                }, ...rest, children: children }, `${id}-${isPortaling}`)] }));
});
const DraggedRootContext = createContext(false);
export function useIsDragPreview() {
    return useContext(DraggedRootContext);
}
/**
 * Animates the movement of the dragged object according to drag gesture.
 * Updates the position of the dragged box and checks for overlapping drop regions.
 * Applies primary overlapped region to the DnD store.
 */
const DraggedRoot = memo(function DraggedRoot({ children, ref, style, Container: UserContainer, ...rest }) {
    const dragged = useDraggableContext();
    const status = useDndStore((state) => state.dragging === dragged.id
        ? 'active'
        : state.candidate === dragged.id
            ? 'candidate'
            : 'inactive');
    const ContainerImpl = UserContainer || DefaultDraggedContainer;
    return (_jsx(DraggedRootContext.Provider, { value: true, children: _jsx(ContainerImpl, { ref: ref, draggable: dragged, status: status, gesture: gesture, ...rest, children: _jsx(AnimatePresence, { children: _jsx(DragMotion, { layoutId: dragged.id, transition: flipTransition, "data-draggable-preview": true, "data-draggable-preview-id": dragged.id, "data-disabled": dragged.disabled, className: "w-full h-full", children: children }) }) }) }));
});
export const DefaultDraggedContainer = ({ children, ref, className, }) => {
    const transform = useCenteredDragTransform(gesture);
    return (_jsx(DragMotion, { style: {
            position: 'absolute',
            transform,
            zIndex: 1000000,
        }, ref: ref, className: className, children: children }));
};
export function useCenteredDragTransform(gesture) {
    const { x, y } = gesture.current;
    const scale = useTransform(() => {
        return gesture.type === 'keyboard' ? 1.1 : 1;
    });
    const transform = useMotionTemplate `translate(-50%, -50%) translate3d(${x}px, ${y}px, 0) scale(${scale})`;
    return transform;
}
//# sourceMappingURL=Draggable.js.map